import React, { useEffect, useState } from "react";
import { db } from "../firebase/firebaseConfig";
import { collection, onSnapshot, updateDoc, doc } from "firebase/firestore";

const JobList = () => {
  const [jobs, setJobs] = useState({ new: [], inProgress: [], complete: [] });
  const [expandedJob, setExpandedJob] = useState(null);
  const [engineerNotes, setEngineerNotes] = useState({});
  const [showArchive, setShowArchive] = useState(false);
  const [alarmPlayed, setAlarmPlayed] = useState(false);
  const [acknowledgingJobId, setAcknowledgingJobId] = useState(null);
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [archiveDateFilter, setArchiveDateFilter] = useState("");

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "machines"), (snapshot) => {
      const jobsData = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));

      const newJobs = jobsData
        .filter((job) => job.status === "new")
        .sort((a, b) => {
          if (a.faultType === "Stoppage" && b.faultType !== "Stoppage") return -1;
          if (a.faultType !== "Stoppage" && b.faultType === "Stoppage") return 1;
          return 0;
        });

      setJobs({
        new: newJobs,
        inProgress: jobsData.filter((job) => job.status === "inprogress"),
        complete: jobsData
          .filter((job) => job.status === "complete")
          .sort((a, b) => {
            const dateA = a.dateOfCompletion?.toDate?.() || new Date(0);
            const dateB = b.dateOfCompletion?.toDate?.() || new Date(0);
            return dateB - dateA; // newest first
        }),

      });

      const hasStoppage = newJobs.some((job) => job.faultType === "Stoppage");
      if (hasStoppage && !alarmPlayed) {
        const audio = new Audio("/alarm.mp3");
        audio.play().catch(() => {});
        setAlarmPlayed(true);
      }

      const notesMap = {};
      jobsData.forEach((job) => {
        if (job.id) {
          notesMap[job.id] = job.engineerNotes || "";
        }
      });
      setEngineerNotes(notesMap);
    });

    return () => unsubscribe();
  }, [alarmPlayed]);

  const handleExpand = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const acknowledgeJob = async (id, engineer) => {
    const jobRef = doc(db, "machines", id);
    await updateDoc(jobRef, {
      status: "inprogress",
      acknowledgedBy: engineer,
      dateOfAcknowledgement: new Date(),
    });
    setAcknowledgingJobId(null);
    setSelectedEngineer("");
  };

  const saveNotes = async (id) => {
    if (!engineerNotes[id]) return;
    const jobRef = doc(db, "machines", id);
    await updateDoc(jobRef, { engineerNotes: engineerNotes[id] });
  };

  const completeJob = async (id) => {
    const jobRef = doc(db, "machines", id);
    await updateDoc(jobRef, {
      status: "complete",
      dateOfCompletion: new Date(),
    });
  };

  const filteredArchiveJobs = archiveDateFilter
  ? jobs.complete.filter((job) => {
      const jobDate = job.dateOfCompletion?.toDate?.();
      if (!jobDate) return false;
      return jobDate.toISOString().slice(0, 10) === archiveDateFilter;
    })
  : jobs.complete;


  const unarchiveJob = async (id) => {
    const jobRef = doc(db, "machines", id);
    await updateDoc(jobRef, {
      status: "inprogress",
      dateOfCompletion: null,
    });
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-100 p-6">
      <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-3xl border border-gray-300">
        <h2 className="text-2xl font-semibold text-center text-gray-800 mb-4">Job Management</h2>

        <JobSection
          title="🆕 New Jobs"
          jobs={jobs.new}
          expandedJob={expandedJob}
          handleExpand={handleExpand}
          acknowledgingJobId={acknowledgingJobId}
          selectedEngineer={selectedEngineer}
          setAcknowledgingJobId={setAcknowledgingJobId}
          setSelectedEngineer={setSelectedEngineer}
          onAcknowledge={acknowledgeJob}
        />

        <JobSection
          title="🔧 In Progress Jobs"
          jobs={jobs.inProgress}
          expandedJob={expandedJob}
          handleExpand={handleExpand}
          jobNotes={engineerNotes}
          setJobNotes={setEngineerNotes}
          actionButtons={(job) => (
            <div className="space-y-2">
              <p className="text-gray-600 text-sm">
                <strong>Acknowledged By:</strong> {job.acknowledgedBy || "N/A"}
              </p>
              <div className="flex justify-between space-x-2">
                <button
                  onClick={() => saveNotes(job.id)}
                  className="w-1/2 bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition text-lg font-semibold"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => completeJob(job.id)}
                  className="w-1/2 bg-green-500 text-white py-2 rounded-md hover:bg-green-600 transition text-lg font-semibold"
                >
                  Complete
                </button>
              </div>
            </div>
          )}
        />

        <button
          onClick={() => setShowArchive(!showArchive)}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
        >
          {showArchive ? "Hide Archive" : "View Archive"}
        </button>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Completion Date:</label>
            <input
              type="date"
              value={archiveDateFilter}
              onChange={(e) => setArchiveDateFilter(e.target.value)}
              className="border border-gray-300 rounded-md p-2 w-full"
            />
          </div>

        {showArchive && (
          
          <JobSection
            title="✅ Completed Jobs (Archive)"
            jobs={filteredArchiveJobs}
            expandedJob={expandedJob}
            handleExpand={handleExpand}
            archiveMode
            actionButton={(job) => (
              <button
                onClick={() => unarchiveJob(job.id)}
                className="w-full bg-gray-500 text-white py-2 rounded-md hover:bg-gray-600 transition text-lg font-semibold"
              >
                Unarchive
              </button>
            )}
          />
        )}
      </div>
    </div>
  );
};

const JobSection = ({
  title,
  jobs,
  expandedJob,
  handleExpand,
  jobNotes,
  setJobNotes,
  actionButtons,
  actionButton,
  archiveMode,
  acknowledgingJobId,
  setAcknowledgingJobId,
  selectedEngineer,
  setSelectedEngineer,
  onAcknowledge,
}) => (
  <div className="mb-6">
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    {jobs.length > 0 ? (
      <ul className="space-y-4">
        {jobs.map((job) => {
          const urgentClass =
            job.status === "new"
              ? job.faultType?.toLowerCase() === "stoppage"
                ? "flash"
                : job.faultType?.toLowerCase() === "service"
                ? "pulse"
                : ""
              : "";

          return (
            <li
              key={job.id}
              className={`p-4 rounded shadow-md ${urgentClass}`}
              style={{ backgroundColor: "#f3f4f6" }}
            >
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => handleExpand(job.id)}
              >
                <div className="text-lg font-medium text-gray-700">
                  {job.machine} - {job.fault}
                </div>
                <span className="text-gray-500">
                  {expandedJob === job.id ? "▲" : "▼"}
                </span>
              </div>

              {expandedJob === job.id && (
                <div className="mt-3 p-3 bg-white rounded border border-gray-300 space-y-3">
                  <p><strong>Reported By:</strong> {job.name}</p>
                  <p><strong>Submitted:</strong> {job.timestamp?.toDate().toLocaleString() || "N/A"}</p>
                  {job.notes && <p><strong>Reported Notes:</strong> {job.notes}</p>}
                  {archiveMode && (
                    <p>
                      <strong>Completed:</strong> {job.dateOfCompletion?.toDate().toLocaleString() || "N/A"}
                    </p>
                  )}
                  {archiveMode && job.engineerNotes && (
                    <p>
                      <strong>Engineer Notes:</strong> {job.engineerNotes}
                    </p>
                  )}
                  {!archiveMode && (
                    <>
                  {job.status === "inprogress" && (
                  <>
                    <label className="block text-gray-700 font-medium">Engineer Notes:</label>
                      <textarea
                        className="w-full p-2 border rounded-md"
                        value={jobNotes?.[job.id] || ""}
                        onChange={(e) =>
                      setJobNotes((prev) => ({
                        ...prev,
                        [job.id]: e.target.value,
                      }))
                    }
                  />
                </>
                )}

                    </>
                  )}

                  {job.status === "new" && acknowledgingJobId === job.id ? (
                    <div className="space-y-2">
                      <label className="block font-medium text-gray-700">Select your name:</label>
                      <select
                        value={selectedEngineer}
                        onChange={(e) => setSelectedEngineer(e.target.value)}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="">-- Select Engineer --</option>
                        {["Raf", "Kate", "John", "Rob", "Martin"].map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => onAcknowledge(job.id, selectedEngineer)}
                        disabled={!selectedEngineer}
                        className={`w-full ${
                          selectedEngineer ? "bg-yellow-500 hover:bg-yellow-600" : "bg-gray-300"
                        } text-white py-2 rounded-md transition text-lg font-semibold`}
                      >
                        Confirm Acknowledgement
                      </button>
                    </div>
                  ) : (
                    actionButtons ? actionButtons(job) : (
                      <button
                        onClick={() => setAcknowledgingJobId(job.id)}
                        className="w-full bg-yellow-500 text-white py-2 rounded-md hover:bg-yellow-600 transition text-lg font-semibold"
                      >
                        Acknowledge
                      </button>
                    )
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    ) : (
      <p className="text-gray-500">No jobs found.</p>
    )}
  </div>
);

export default JobList;
